import React, { useMemo, useEffect } from "react";
import {
  Input,
  Select,
  Checkbox,
  Radio,
  Table,
  TreeSelect,
  Typography,
  Space,
  Spin,
} from "antd";
import { FormField, SpaceBetween, Box } from "@cloudscape-design/components";
import { useController, Control, FieldError } from "react-hook-form";
import { FormField as FormFieldType } from "../../../types/formSchema";
import { FIELD_EVENTS, StaticFilterValues } from "../../../constants/StaticFilterValues";
import { CURRENCY_CONFIG } from "../../../constants/currencyConfig";

const { TextArea } = Input;

interface FieldComponentProps {
  field: FormFieldType;
  control: Control<any>;
  dynamicOptions?: Record<string, any[]>;
  onFieldChange?: (fieldName: string, value: any) => void;
}

// Custom validation rule converter
const convertValidationRules = (field: FormFieldType) => {
  const rules: any = {};

  if (field.validation) {
    field.validation.forEach((rule) => {
      if (rule.required) {
        rules.required = rule.message || `${field.label} is required`;
      }
      if (rule.min) {
        rules.minLength = {
          value: rule.min,
          message: rule.message || `Minimum length is ${rule.min}`,
        };
      }
      if (rule.max) {
        rules.maxLength = {
          value: rule.max,
          message: rule.message || `Maximum length is ${rule.max}`,
        };
      }
      if (rule.pattern) {
        rules.pattern = {
          value: new RegExp(rule.pattern),
          message: rule.message || "Invalid format",
        };
      }
    });
  }

  return rules;
};

// Text Input Field
export const TextFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Input
        {...componentProps}
        {...controllerField}
        placeholder={field.placeholder}
        disabled={!!field.disabled}
        readOnly={!!field.readonly}
        status={error ? "error" : ""}
        onChange={(event) => {
          const value = event.target.value;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      />
    </FormField>
  );
};

// Textarea Field
export const TextAreaFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const textAreaField = field as any;

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <TextArea
        {...componentProps}
        {...controllerField}
        placeholder={field.placeholder}
        disabled={!!field.disabled}
        readOnly={!!field.readonly}
        status={error ? "error" : ""}
        rows={textAreaField.rows || 4}
        onChange={(event) => {
          const value = event.target.value;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      />
    </FormField>
  );
};

// Dropdown Field
export const DropdownFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const options = dynamicOptions?.[field.name] || (field as any).options || [];

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Select
        {...componentProps}
        {...controllerField}
        placeholder={field.placeholder || "Select an option"}
        disabled={!!field.disabled || !!field.readonly}
        status={error ? "error" : ""}
        style={{ width: "100%", ...componentProps.style }}
        onChange={(value) => {
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
        options={options.map((option: any) => ({
          label: option.label,
          value: option.value,
        }))}
      />
    </FormField>
  );
};

// Cascading Dropdown Field
export const CascadingDropdownFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const options = dynamicOptions?.[field.name] || [];
  const isDisabled =
    Boolean(field.disabled) ||
    (Boolean(field.dependsOn) && options.length === 0);

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Select
        {...componentProps}
        {...controllerField}
        placeholder={
          field.dependsOn && options.length === 0
            ? `Select ${field.dependsOn} first`
            : field.placeholder || "Select an option"
        }
        disabled={isDisabled || !!field.readonly}
        status={error ? "error" : ""}
        style={{ width: "100%", ...componentProps.style }}
        loading={Boolean(options.length === 0 && field.dependsOn)}
        onChange={(value) => {
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
        options={options.map((option: any) => ({
          label: option.label,
          value: option.value,
        }))}
      />
    </FormField>
  );
};

// Checkbox Field
export const CheckboxFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;

  return (
    <SpaceBetween size="xs">
      <Checkbox
        {...componentProps}
        {...controllerField}
        checked={controllerField.value || false}
        disabled={Boolean(field.disabled)}
        onChange={(event) => {
          const value = event.target.checked;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      >
        {field.label}
      </Checkbox>
      {field.tooltip && (
        <Box color="text-body-secondary" fontSize="body-s">
          {field.tooltip}
        </Box>
      )}
      {error && (
        <Box color="text-status-error" fontSize="body-s">
          {error.message}
        </Box>
      )}
    </SpaceBetween>
  );
};

// Radio Group Field
export const RadioGroupFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const radioField = field as any;
  const options = radioField.options || [];

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Radio.Group
        {...componentProps}
        {...controllerField}
        onChange={(event) => {
          const value = event.target.value;
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
      >
        <Space direction="vertical">
          {options.map((option: any) => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </FormField>
  );
};

// Multiselect Field
export const MultiselectFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const multiselectField = field as any;
  const options = dynamicOptions?.[field.name] || multiselectField.options || [];
  const maxTagCount = multiselectField.maxTagCount || 5;

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Select
        {...componentProps}
        mode="multiple"
        {...controllerField}
        placeholder={field.placeholder || "Select options"}
        disabled={!!field.disabled || !!field.readonly}
        status={error ? "error" : ""}
        style={{ width: "100%", ...componentProps.style }}
        maxTagCount={maxTagCount}
        onChange={(values) => {
          controllerField.onChange(values);
          onFieldChange?.(field.name, values);
        }}
        options={options.map((option: any) => ({
          label: option.label,
          value: option.value,
        }))}
      />
    </FormField>
  );
};

// TreeSelect Field
export const TreeSelectFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  dynamicOptions,
  onFieldChange,
}) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const treeSelectField = field as any;

  // Get tree data from dynamic options or field configuration
  const treeData =
    dynamicOptions?.[field.name] || treeSelectField.treeData || [];

  // Get loading nodes set from dynamic options
  const loadingNodes = dynamicOptions?.[`${field.name}_loadingNodes`] || new Set();
  const isSearchLoading = dynamicOptions?.[`${field.name}_searchLoading`] || false;

  // Custom switcher icon - show spinner only for nodes that are loading
  const switcherIcon = ({ isLeaf, expanded, loading, data }: any) => {
    const nodeKey = data?.key || data?.value;
    if (loading || (loadingNodes instanceof Set && loadingNodes.has(nodeKey))) {
      return <Spin size="small" />;
    }
    return undefined;
  };

  // Check if multiple selection is enabled
  const isMultiple = treeSelectField.multiple || false;

  const maxTagCount = treeSelectField.maxTagCount || 5;

  // Handle loadData for dynamic loading
  const handleLoadData = treeSelectField.loadData ? (node: any) => {
    if (treeSelectField.filterType && onFieldChange) {
      return new Promise<void>((resolve) => {
        onFieldChange(`${field.name}${FIELD_EVENTS.LOAD_DATA}`, { node, filterType: treeSelectField.filterType });
        resolve();
      });
    }
    return Promise.resolve();
  } : undefined;

  const handleSearch = treeSelectField.filterType === StaticFilterValues.PRODUCT_ID ? (searchValue: string) => {
    if (onFieldChange) {
      onFieldChange(`${field.name}${FIELD_EVENTS.SEARCH}`, { searchValue, filterType: treeSelectField.filterType });
    }
  } : undefined;

  const filterTreeNode = treeSelectField.filterType === StaticFilterValues.PRODUCT_ID ? false : true;

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <TreeSelect
        {...componentProps}
        {...controllerField}
        placeholder={
          field.placeholder ||
          (isMultiple ? "Select options" : "Select an option")
        }
        disabled={!!field.disabled || !!field.readonly}
        status={error ? "error" : ""}
        multiple={isMultiple}
        treeData={treeData}
        showSearch
        allowClear
        treeDefaultExpandAll={treeSelectField.treeDefaultExpandAll ?? true}
        treeCheckable={isMultiple}
        showCheckedStrategy={isMultiple ? TreeSelect.SHOW_PARENT : undefined}
        maxTagCount={maxTagCount}
        loadData={handleLoadData}
        onSearch={handleSearch}
        filterTreeNode={filterTreeNode}
        notFoundContent={(loadingNodes instanceof Set && loadingNodes.size > 0) || isSearchLoading ? <Spin size="small" /> : (treeSelectField.filterType === StaticFilterValues.PRODUCT_ID ? "" : "No options available")}
        switcherIcon={switcherIcon}
        onChange={(value) => {
          controllerField.onChange(value);
          onFieldChange?.(field.name, value);
        }}
        style={{ width: "100%" }}
      />
    </FormField>
  );
};

// Document Sections Table Field - Tree table with hierarchical structure
export const DocumentSectionsTableFieldComponent: React.FC<
  FieldComponentProps
> = ({ field, control, dynamicOptions, onFieldChange }) => {
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: convertValidationRules(field),
  });

  const { componentProps = {} } = field;
  const tableField = field as any;
  const tableData = tableField.tableData || [];
  const currentValue = controllerField.value || {};

  // Apply dynamic children if available
  const processedTableData = useMemo(() => {
    return tableData.map((item: any) => {
      const dynamicChildrenKey = `${field.name}_${item.id}_children`;
      if (dynamicOptions?.[dynamicChildrenKey]) {
        return {
          ...item,
          children: dynamicOptions[dynamicChildrenKey]
        };
      }
      return item;
    });
  }, [tableData, dynamicOptions, field.name]);

  // Get all leaf nodes (children) for bulk selection logic
  const getAllLeafNodes = (nodes: any[]): any[] => {
    let leafNodes: any[] = [];
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        leafNodes = leafNodes.concat(getAllLeafNodes(node.children));
      } else {
        leafNodes.push(node);
      }
    });
    return leafNodes;
  };

  // Reset P&L by GL filter when product line changes
  useEffect(() => {
    const resetSignal = dynamicOptions?.['documentSections_executive_pnl_pnl_by_gl_reset'];
    const currentFilterValue = currentValue['pl_gl']?.filterValue;
    
    if (resetSignal && currentFilterValue !== undefined) {
      const newValue = {
        ...currentValue,
        'executive_pnl_pnl_by_gl': {
          ...currentValue['executive_pnl_pnl_by_gl'],
          filterValue: undefined
        }
      };
      controllerField.onChange(newValue);
      onFieldChange?.(field.name, newValue);
    }
  }, [dynamicOptions?.['documentSections_executive_pnl_pnl_by_gl_reset']]);

  // Memoize expensive calculations
  const leafNodes = useMemo(() => getAllLeafNodes(processedTableData), [processedTableData]);

  // Get checkbox states for bulk selection
  const getCheckboxStates = (
    checkboxType: "includeTable" | "includeCommentary"
  ) => {
    const states = leafNodes.map(
      (item: any) => currentValue[item.id]?.[checkboxType] ?? item[checkboxType]
    );
    const checkedCount = states.filter(Boolean).length;

    return {
      allChecked: checkedCount === leafNodes.length,
      someChecked: checkedCount > 0 && checkedCount < leafNodes.length,
      noneChecked: checkedCount === 0,
    };
  };

  // Memoize checkbox states to avoid recalculation on every render
  const tableCheckboxStates = useMemo(() => ({
    includeTable: getCheckboxStates("includeTable"),
    includeCommentary: getCheckboxStates("includeCommentary")
  }), [leafNodes, currentValue]);

  // Handle individual checkbox changes
  const handleCheckboxChange = (
    itemId: string,
    checkboxType: "includeTable" | "includeCommentary",
    checked: boolean
  ) => {
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        [checkboxType]: checked,
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
  };

  // Handle bulk checkbox changes (header checkbox)
  const handleBulkCheckboxChange = (
    checkboxType: "includeTable" | "includeCommentary",
    checked: boolean
  ) => {
    const newValue = { ...currentValue };

    leafNodes.forEach((item: any) => {
      newValue[item.id] = {
        ...newValue[item.id],
        [checkboxType]: checked,
      };
    });

    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
  };

  // Handle currency dropdown changes
  const handleCurrencyChange = (itemId: string, selectedValue: string) => {
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        currency: selectedValue,
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
  };

  // Handle filter dropdown changes
  const handleDropdownChange = (itemId: string, selectedValue: string | string[]) => {
    // For multiselect, limit to 10 selections
    let finalValue = selectedValue;
    if (Array.isArray(selectedValue) && selectedValue.length > 10) {
      finalValue = selectedValue.slice(0, 10);
    }
    
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        filterValue: finalValue,
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
  };

  // Handle filter multiselect changes
  const handleMultiselectChange = (itemId: string, selectedValues: string[]) => {
    const newValue = {
      ...currentValue,
      [itemId]: {
        ...currentValue[itemId],
        filterValue: selectedValues,
      },
    };
    controllerField.onChange(newValue);
    onFieldChange?.(`${field.name}_${itemId}_filter`, selectedValues);
  };

  // Get dropdown options for a specific item
  const getDropdownOptions = (item: any) => {
    if (item.filterDependsOn && dynamicOptions?.[`${field.name}_${item.id}`]) {
      return dynamicOptions[`${field.name}_${item.id}`];
    }
    return item.filterOptions || [];
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (text: string, record: any) => {
        // Parent nodes (with children) should be bold
        const isBold = record.isParent === true;
        return <Box fontWeight={isBold ? "bold" : "normal"}>{text}</Box>;
      },
    },
    {
      title: "Filter",
      key: "filter",
      width: 220,
      render: (_: any, record: any) => {
        // Show dash only if no filter
        if (!record.hasFilter) {
          return <Box>—</Box>;
        }

        const options = getDropdownOptions(record);
        const isDisabled = record.filterDependsOn && options.length === 0;
        const isMultiselect = record.filterType === 'multiselect';
        const currentSelections = currentValue[record.id]?.filterValue || [];
        const isMaxReached = isMultiselect && Array.isArray(currentSelections) && currentSelections.length >= 10;
        
        // Check if this specific filter is loading
        const isLoading = Boolean(dynamicOptions?.[`${field.name}_${record.id}_loading`]);

        // Handle multiselect filter type
        if (record.filterType === 'multiselect') {
          return (
            <Select
              mode="multiple"
              value={currentValue[record.id]?.filterValue || []}
              onChange={(values) => handleMultiselectChange(record.id, values)}
              options={options.map((opt: any) => ({
                label: opt.label,
                value: opt.value,
              }))}
              placeholder={record.filterPlaceholder || "Select..."}
              style={{ width: "100%" }}
              maxTagCount={2}
              loading={isLoading}
              disabled={isDisabled || isLoading}
            />
          );
        }

        // Default dropdown filter
        return (
          <Select
            value={currentValue[record.id]?.filterValue || undefined}
            onChange={(value) => handleDropdownChange(record.id, value)}
            options={options.map((opt: any) => ({
              label: opt.label,
              value: opt.value,
              disabled: isMultiselect && Array.isArray(currentSelections) && 
                       currentSelections.length >= 10 && 
                       !currentSelections.includes(opt.value)
            }))}
            placeholder={record.filterPlaceholder || "Select..."}
            disabled={isDisabled || isLoading}
            loading={isLoading}
            mode={isMultiselect ? "multiple" : undefined}
            maxCount={isMultiselect ? 10 : undefined}
            status={isMaxReached ? "warning" : undefined}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: () => {
        const { allChecked, someChecked } = tableCheckboxStates.includeTable;
        return (
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={(event) => {
              handleBulkCheckboxChange("includeTable", event.target.checked);
            }}
          >
            Include Table
          </Checkbox>
        );
      },
      key: "includeTable",
      width: 150,
      render: (_: any, record: any) => {
        // Only show checkboxes for leaf nodes (children)
        if (record.isParent === true) {
          return null;
        }

        return (
          <Checkbox
            checked={
              currentValue[record.id]?.includeTable ?? record.includeTable
            }
            onChange={(event) => {
              handleCheckboxChange(
                record.id,
                "includeTable",
                event.target.checked
              );
            }}
          />
        );
      },
    },
    {
      title: () => {
        const { allChecked, someChecked } = tableCheckboxStates.includeCommentary;
        return (
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={(event) => {
              handleBulkCheckboxChange(
                "includeCommentary",
                event.target.checked
              );
            }}
          >
            Include Commentary
          </Checkbox>
        );
      },
      key: "includeCommentary",
      width: 200,
      render: (_: any, record: any) => {
        // Only show checkboxes for leaf nodes (children)
        if (record.isParent === true) {
          return null;
        }

        return (
          <Checkbox
            checked={
              currentValue[record.id]?.includeCommentary ??
              record.includeCommentary
            }
            onChange={(event) => {
              handleCheckboxChange(
                record.id,
                "includeCommentary",
                event.target.checked
              );
            }}
          />
        );
      },
    },
    {
      title: "Currency",
      key: "currency",
      width: 140,
      render: (_: any, record: any) => {
        // Only show currency dropdown for leaf nodes (children)
        if (record.isParent === true) {
          return null;
        }

        return (
          <Select
            value={
              currentValue[record.id]?.currency ?? record.currency ?? CURRENCY_CONFIG.DEFAULT
            }
            onChange={(value) => handleCurrencyChange(record.id, value)}
            options={[...CURRENCY_CONFIG.OPTIONS]}
            style={{ width: "100%" }}
          />
        );
      },
    },
  ];

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <Table
        {...componentProps}
        columns={columns}
        dataSource={processedTableData}
        rowKey="id"
        pagination={false}
        size="small"
        bordered
        expandable={{ defaultExpandAllRows: true }}
        style={{ width: "100%" }}
      />
    </FormField>
  );
};

// Field Registry - Maps field types to components
export const FieldRegistry = {
  text: TextFieldComponent,
  textarea: TextAreaFieldComponent,
  dropdown: DropdownFieldComponent,
  cascadingDropdown: CascadingDropdownFieldComponent,
  checkbox: CheckboxFieldComponent,
  radioGroup: RadioGroupFieldComponent,
  multiselect: MultiselectFieldComponent,
  treeSelect: TreeSelectFieldComponent,
  documentSectionsTable: DocumentSectionsTableFieldComponent,
} as const;

export type FieldType = keyof typeof FieldRegistry;
